import React from 'react';
import { Link } from 'react-router-dom';

const GRID_BG = {
  backgroundImage:
    'linear-gradient(rgba(201,168,76,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.12) 1px, transparent 1px)',
  backgroundSize: '60px 60px',
};

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-lg font-black text-navy-800 font-heading uppercase tracking-wider border-b-2 border-gold-500 pb-2 mb-5">
      {title}
    </h2>
    {children}
  </div>
);

const BulletItem = ({ children }) => (
  <li className="flex items-start gap-2.5 text-slate-600 text-sm mb-2.5">
    <span className="text-gold-500 mt-0.5 shrink-0 font-bold">›</span>
    {children}
  </li>
);

const CapabilityStatement = () => {
  return (
    <div className="pt-20">

      {/* Page header */}
      <section className="relative bg-navy-800 py-20 overflow-hidden">
        <div className="absolute inset-0" style={GRID_BG} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-slate-400 mb-6">
            <Link to="/" className="hover:text-gold-400 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-gold-400">Capability Statement</span>
          </nav>
          <p className="text-gold-500 text-sm font-bold uppercase tracking-widest mb-4">About DTX</p>
          <h1 className="text-4xl md:text-5xl font-black text-white font-heading mb-6">Capability Statement</h1>
          <p className="text-slate-300 mb-8 max-w-xl">
            An overview of DTX Digital Transformation's core capabilities, certifications, and differentiators
            for federal procurement officers.
          </p>
          {/* Replace href with the actual path to your uploaded PDF */}
          <a
            href="/capability-statement.pdf"
            download
            className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-navy-900 px-6 py-3 rounded-xl font-bold transition-colors shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download PDF
          </a>
        </div>
      </section>

      {/* Capability content */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Company snapshot */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 mb-10">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 text-sm">
              {[
                { label: 'Company Name', value: 'DTX Digital Transformation, LLC' },
                { label: 'Business Type', value: 'Limited Liability Company (LLC)' },
                { label: 'HQ Location', value: '43661 Catton Pl, Ashburn, VA 20147' },
                { label: 'SAM.gov UEI', value: 'ESNGY5HK7QS1' },
                { label: 'CAGE Code', value: '64GE5' },
                { label: 'Established', value: '2009' },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">{label}</p>
                  <p className="text-navy-800 font-semibold">{value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-gray-100">

            <Section title="Core Capabilities">
              <ul className="list-none">
                <BulletItem>
                  <span><strong className="text-navy-800">IT Modernization & Project Management:</strong> Agile/Scrum, PMBOK, PRINCE2, IV&V, program governance, and stakeholder engagement across federal IT programs.</span>
                </BulletItem>
                <BulletItem>
                  <span><strong className="text-navy-800">Cybersecurity:</strong> SSP development, vulnerability assessments, penetration testing, EDR deployment, and security metrics aligned to NIST 800-53 and CMMC.</span>
                </BulletItem>
                <BulletItem>
                  <span><strong className="text-navy-800">Governance, Risk & Compliance (GRC):</strong> FISMA/FISCAM/SSAE 18 audit support, POA&M management, ERM, and COSO/COBIT/NIST framework implementation.</span>
                </BulletItem>
                <BulletItem>
                  <span><strong className="text-navy-800">DevOps:</strong> CI/CD pipelines, development in C, C++, C#, Java, Python, Swift; Oracle and Appian platform support; Infrastructure as Code.</span>
                </BulletItem>
                <BulletItem>
                  <span><strong className="text-navy-800">Facilities Management & Space Planning:</strong> Space utilization, move management, signage, help desk, and IWMS/CAFM/CMMS implementation for federal facilities.</span>
                </BulletItem>
              </ul>
            </Section>

            <Section title="Certifications & Set-Asides">
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { cert: 'SBA 8(a) Certified', detail: 'Sole-source eligible up to $4.5M (services)' },
                  { cert: 'EDWOSB', detail: 'Economically Disadvantaged WOSB' },
                  { cert: 'WOSB', detail: 'Women-Owned Small Business' },
                  { cert: 'SAM.gov Registered', detail: 'Active federal registration' },
                ].map(({ cert, detail }) => (
                  <div key={cert} className="flex items-start gap-3 bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <div className="w-2 h-2 bg-gold-500 rounded-full mt-1.5 shrink-0" />
                    <div>
                      <p className="font-bold text-navy-800 text-sm">{cert}</p>
                      <p className="text-slate-500 text-xs mt-0.5">{detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Key Differentiators">
              <ul className="list-none">
                <BulletItem>100% Woman-Owned — priority consideration across multiple federal set-aside categories.</BulletItem>
                <BulletItem>Agile small business structure enables direct access to senior leadership and faster decision-making.</BulletItem>
                <BulletItem>Dual-service model (IT + Facilities) enables integrated, cross-functional solutions under one contract vehicle.</BulletItem>
                <BulletItem>Deep familiarity with federal acquisition regulations, FAR/DFAR compliance, and agency reporting requirements.</BulletItem>
                <BulletItem>Commitment to on-time, on-budget delivery with measurable performance outcomes.</BulletItem>
              </ul>
            </Section>

            <Section title="Contact Information">
              <div className="grid sm:grid-cols-2 gap-4 text-sm text-slate-600">
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Email</p>
                  <a href="mailto:contact@dtxdigitaltransformation.com" className="text-gold-600 hover:text-gold-700 font-medium">
                    contact@dtxdigitaltransformation.com
                  </a>
                </div>
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Website</p>
                  <p className="font-medium">www.dtxdigitaltransformation.com</p>
                </div>
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Phone</p>
                  <p className="font-medium">703-328-2940 / 703-328-2748</p>
                </div>
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Address</p>
                  <p className="font-medium">43661 Catton Pl, Ashburn, VA 20147</p>
                </div>
              </div>
            </Section>

          </div>
        </div>
      </section>

    </div>
  );
};

export default CapabilityStatement;
